import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ClockIcon,
  CheckCircleIcon,
  BellIcon,
  DocumentTextIcon,
  CurrencyDollarIcon,
  MagnifyingGlassIcon,
  ArrowTrendingUpIcon,
  DocumentDuplicateIcon,
  BanknotesIcon,
  SparklesIcon,
  PhoneIcon,
  EnvelopeIcon,
  UserIcon,
  ArrowDownTrayIcon,
  DocumentCheckIcon,
  ExclamationCircleIcon,
  ChartBarIcon,
  CalculatorIcon,
  GlobeAltIcon,
  UserGroupIcon,
  TicketIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';
import WebAnalytics from '../components/admin/WebAnalytics';
import CRM from '../components/admin/CRM';
import SupportTickets from '../components/admin/SupportTickets';
import UserManagement from './Admin/UserManagement';

interface Deal {
  id: string;
  companyName: string;
  ticker: string;
  facilitySize: number;
  status: 'lead_in' | 'docs_out' | 'awaiting_registration' | 'active' | 'completed';
  stage: string;
  daysInStage: number;
  contact: {
    name: string;
    phone: string;
  };
  lastActivity: Date;
  nextAction: string;
  progress: number;
}

interface Exercise {
  id: string;
  facilityId: string;
  companyName: string;
  ticker: string;
  type: 'VWAP' | 'Rapid' | 'Advance';
  shares: number;
  estimatedAmount: number;
  submittedDate: Date;
  valuationStartDate: Date;
  valuationEndDate: Date;
  settlementDate: Date;
  status: 'active' | 'completed';
  volumeThreshold: number;
  currentVolume: number;
  volumeProgress: number;
  finalPrice?: number;
  finalProceeds?: number;
  wireConfirmation?: string;
  tradingData?: any;
  pnl?: number;
}

interface Document {
  id: string;
  dealId: string;
  companyName: string;
  type: 'facility_agreement' | 'amendment' | 'term_sheet' | 'other';
  title: string;
  status: 'pending_signature' | 'executed' | 'inactive';
  uploadedDate: Date;
  executedDate?: Date;
  fileUrl: string;
}

interface Activity {
  id: string;
  type: 'exercise_notice' | 'document_signed' | 'registration_filed' | 'settlement_complete' | 'task_complete';
  dealId: string;
  companyName: string;
  description: string;
  timestamp: Date;
  priority: 'high' | 'medium' | 'low';
  user?: string;
}

interface Task {
  id: string;
  dealId: string;
  companyName: string;
  title: string;
  description: string;
  assignee?: string;
  dueDate: Date;
  status: 'pending' | 'in_progress' | 'completed';
  progress: number;
  type: 'stock_transfer' | 'document_review' | 'compliance_check' | 'settlement';
}

interface Transaction {
  id: string;
  companyName: string;
  ticker: string;
  facilityId: string;
  facilitySize: number;
  transactionType: string;
  status: string;
  stage: string;
  sharesRequested: number;
  exercisePrice: number;
  currentPrice: number;
  potentialProceeds: number;
  exerciseDate: string;
  daysSinceExercise: number;
  valuationPeriodStart: string;
  valuationPeriodEnd: string;
  hoursRemainingInValuation: number;
  tradingSessionsRemaining: number;
  volumeThreshold: number;
  cumulativeVolume: number;
  volumeProgress: number;
  volumeThresholdMet: boolean;
  vwapCalculationPeriod: string;
  facilityDiscount: number;
  advanceDrawBalance: number;
  exchangeCapDeduction: number;
  elocDeduction: number;
  exerciseNoticeStatus: string;
  isExchangeCapExhausted: boolean;
  automaticFloorPriceActive: boolean;
  contact: {
    name: string;
    email: string;
    phone: string;
  };
  lastActivity: string;
  nextAction: string;
  progress: number;
  settlementPeriodStart?: string;
  settlementPeriodEnd?: string;
  settlementStatement?: any;
  volumeChecksCount: number;
  vwapHistoryCount: number;
  repaymentNoticesCount: number;
  volumeUpdatesCount: number;
}

interface Facility {
  id: string;
  companyName: string;
  ticker: string;
  facilitySize: number;
  status: 'lead_in' | 'docs_out' | 'awaiting_registration' | 'active' | 'completed';
  stage: string;
  daysInStage: number;
  contact: {
    name: string;
    phone: string;
  };
  lastActivity: string;
  nextAction: string;
  progress: number;
  exercises: any[];
}

interface PipelineColumn {
  id: string;
  title: string;
  deals: Deal[];
  color: string;
  bgColor: string;
  value: number;
  probability: number;
}

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState<'documents' | 'activities' | 'tasks' | 'analytics' | 'web-analytics' | 'crm' | 'support' | 'facilities' | 'transactions' | 'users'>('facilities');
  const [deals, setDeals] = useState<Deal[]>([]);
  const [exercises, setExercises] = useState<Exercise[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [facilitySearchTerm, setFacilitySearchTerm] = useState('');
  const [facilityStatusFilter, setFacilityStatusFilter] = useState<string>('all');
  const [facilityViewMode, setFacilityViewMode] = useState<'table' | 'pipeline'>('table');
  // const [exerciseViewMode, setExerciseViewMode] = useState<'active' | 'completed'>('active'); // Temporarily commented - used in exercise tab
  const [transactionSearchTerm, setTransactionSearchTerm] = useState('');
  const [transactionStatusFilter, setTransactionStatusFilter] = useState<string>('all');
  const [transactionSharesFilter, setTransactionSharesFilter] = useState<string>('all');
  const [transactionVolumeFilter, setTransactionVolumeFilter] = useState<string>('all');
  const [transactionDateFilter, setTransactionDateFilter] = useState<string>('all');
  const [draggedDeal, setDraggedDeal] = useState<Deal | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);
  const [selectedFacility, setSelectedFacility] = useState<Facility | null>(null);
  const [showFacilityModal, setShowFacilityModal] = useState(false);

  const pipelineStages: PipelineColumn[] = [
    {
      id: 'lead_in',
      title: 'Lead In',
      deals: [],
      color: 'text-gray-700',
      bgColor: 'bg-gray-100 hover:bg-gray-200',
      value: 0,
      probability: 10
    },
    {
      id: 'docs_out',
      title: 'Docs Out',
      deals: [],
      color: 'text-blue-700',
      bgColor: 'bg-blue-100 hover:bg-blue-200',
      value: 0,
      probability: 40
    },
    {
      id: 'awaiting_registration',
      title: 'Awaiting Registration',
      deals: [],
      color: 'text-orange-700',
      bgColor: 'bg-orange-100 hover:bg-orange-200',
      value: 0,
      probability: 80
    },
    {
      id: 'active',
      title: 'Active',
      deals: [],
      color: 'text-green-700',
      bgColor: 'bg-green-100 hover:bg-green-200',
      value: 0,
      probability: 100
    },
    {
      id: 'completed',
      title: 'Completed',
      deals: [],
      color: 'text-purple-700',
      bgColor: 'bg-purple-100 hover:bg-purple-200',
      value: 0,
      probability: 100
    }
  ];

  // Transform facilities to deals
  const transformFacilitiesToDeals = useCallback((facilitiesData: Facility[]): Deal[] => {
    return facilitiesData.map(facility => ({
      id: facility.id,
      companyName: facility.companyName,
      ticker: facility.ticker,
      facilitySize: facility.facilitySize,
      status: facility.status as Deal['status'],
      stage: facility.stage,
      daysInStage: facility.daysInStage,
      contact: {
        name: facility.contact.name,
        phone: facility.contact.phone
      },
      lastActivity: new Date(facility.lastActivity),
      nextAction: facility.nextAction,
      progress: facility.progress
    }));
  }, []);

  const fetchAdminData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch all facilities with their status
      const facilitiesRes = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/admin/facilities`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (facilitiesRes.data.success && facilitiesRes.data.data) {
        const facilitiesData = facilitiesRes.data.data;
        setFacilities(facilitiesData);
        // Transform facilities to deals
        setDeals(transformFacilitiesToDeals(facilitiesData));
      }

      // Fetch recent activities
      const activitiesRes = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/admin/activities`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (activitiesRes.data.success && activitiesRes.data.data) {
        setActivities(activitiesRes.data.data);
      } else {
        // setActivities(getMockActivities());
      }

      // Fetch tasks
      const tasksRes = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/admin/tasks`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (tasksRes.data.success && tasksRes.data.data) {
        setTasks(tasksRes.data.data);
      } else {
        setTasks(getMockTasks());
      }

      // Fetch exercises
      const exercisesRes = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/admin/exercises`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (exercisesRes.data.success && exercisesRes.data.data) {
        setExercises(exercisesRes.data.data);
      } else {
        setExercises(getMockExercises());
      }

      // Fetch documents
      const documentsRes = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/admin/documents`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (documentsRes.data.success && documentsRes.data.data) {
        setDocuments(documentsRes.data.data);
      } else {
      setDocuments(getMockDocuments());
      }

      // Fetch transactions
      const transactionsRes = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/admin/transactions`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (transactionsRes.data.success && transactionsRes.data.data) {
        setTransactions(transactionsRes.data.data);
      }

      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      // Use mock data as fallback
      // setDeals(getMockDeals());
      setActivities(getMockActivities());
      setTasks(getMockTasks());
      setExercises(getMockExercises());
      setDocuments(getMockDocuments());
      setIsLoading(false);
    }
  }, [transformFacilitiesToDeals]);

  useEffect(() => {
    fetchAdminData();
    // Set up real-time updates
    const interval = setInterval(fetchAdminData, 30000); // Update every 30 seconds
    
    // Update volume progress every 15 minutes
    const volumeInterval = setInterval(() => {
      setExercises(prev => prev.map(exercise => {
        if (exercise.status === 'active') {
          // Simulate volume progress update
          const newProgress = Math.min(exercise.volumeProgress + Math.random() * 10, 100);
          return { ...exercise, volumeProgress: newProgress };
        }
        return exercise;
      }));
    }, 15 * 60 * 1000); // 15 minutes

    return () => {
      clearInterval(interval);
      clearInterval(volumeInterval);
    };
  }, [fetchAdminData]);

  // const getMockDeals = (): Deal[] => [
  //   {
  //     id: '1',
  //     companyName: 'BioSig Technologies',
  //     ticker: 'BSGM',
  //     facilitySize: 200000000,
  //     status: 'active',
  //     stage: 'Active Facility',
  //     daysInStage: 45,
  //     contact: {
  //       name: 'John Smith',
  //       phone: '(555) 123-4567'
  //     },
  //     lastActivity: new Date('2024-04-20'),
  //     nextAction: 'Monitor daily eligibility',
  //     progress: 75
  //   },
  //   {
  //     id: '2',
  //     companyName: 'TechCorp Solutions',
  //     ticker: 'TECH',
  //     facilitySize: 50000000,
  //     status: 'awaiting_registration',
  //     stage: 'Registration Pending',
  //     daysInStage: 5,
  //     contact: {
  //       name: 'Sarah Johnson',
  //       phone: '(555) 234-5678'
  //     },
  //     lastActivity: new Date('2024-04-19'),
  //     nextAction: 'Follow up on S-1 filing',
  //     progress: 60
  //   },
  //   {
  //     id: '3',
  //     companyName: 'Green Energy Inc',
  //     ticker: 'GRNE',
  //     facilitySize: 100000000,
  //     status: 'docs_out',
  //     stage: 'Documents Sent',
  //     daysInStage: 2,
  //     contact: {
  //       name: 'Mike Williams',
  //       phone: '(555) 345-6789'
  //     },
  //     lastActivity: new Date('2024-04-18'),
  //     nextAction: 'Schedule review call',
  //     progress: 25
  //   },
  //   {
  //     id: '4',
  //     companyName: 'HealthTech Pro',
  //     ticker: 'HTPR',
  //     facilitySize: 75000000,
  //     status: 'lead_in',
  //     stage: 'Initial Contact',
  //     daysInStage: 1,
  //     contact: {
  //       name: 'Lisa Chen',
  //       phone: '(555) 456-7890'
  //     },
  //     lastActivity: new Date('2024-04-21'),
  //     nextAction: 'Send initial proposal',
  //     progress: 10
  //   }
  // ];

  const getMockExercises = (): Exercise[] => [
    {
      id: 'ex1',
      facilityId: '1',
      companyName: 'BioSig Technologies',
      ticker: 'BSGM',
      type: 'VWAP',
      shares: 500000,
      estimatedAmount: 5000000,
      submittedDate: new Date('2024-04-20T10:00:00'),
      valuationStartDate: new Date('2024-04-21'),
      valuationEndDate: new Date('2024-04-25'),
      settlementDate: new Date('2024-04-26'),
      status: 'active',
      volumeThreshold: 1000000,
      currentVolume: 650000,
      volumeProgress: 65
    },
    {
      id: 'ex2',
      facilityId: '1',
      companyName: 'BioSig Technologies',
      ticker: 'BSGM',
      type: 'Rapid',
      shares: 250000,
      estimatedAmount: 2500000,
      submittedDate: new Date('2024-04-15T14:00:00'),
      valuationStartDate: new Date('2024-04-16'),
      valuationEndDate: new Date('2024-04-16'),
      settlementDate: new Date('2024-04-17'),
      status: 'completed',
      volumeThreshold: 500000,
      currentVolume: 500000,
      volumeProgress: 100,
      finalPrice: 9.85,
      finalProceeds: 2462500,
      wireConfirmation: 'WR-2024041701',
      tradingData: { avgSalePrice: 10.15 },
      pnl: 75000
    },
    {
      id: 'ex3',
      facilityId: '2',
      companyName: 'TechCorp Solutions',
      ticker: 'TECH',
      type: 'Advance',
      shares: 100000,
      estimatedAmount: 1000000,
      submittedDate: new Date('2024-04-19T09:00:00'),
      valuationStartDate: new Date('2024-04-19'),
      valuationEndDate: new Date('2024-04-19'),
      settlementDate: new Date('2024-04-20'),
      status: 'active',
      volumeThreshold: 0,
      currentVolume: 0,
      volumeProgress: 100
    }
  ];

  const getMockDocuments = (): Document[] => [
    {
      id: 'doc1',
      dealId: '1',
      companyName: 'BioSig Technologies',
      type: 'facility_agreement',
      title: 'Equity Line Purchase Agreement',
      status: 'executed',
      uploadedDate: new Date('2024-03-01'),
      executedDate: new Date('2024-03-05'),
      fileUrl: '/documents/biosig-elpa.pdf'
    },
    {
      id: 'doc2',
      dealId: '2',
      companyName: 'TechCorp Solutions',
      type: 'term_sheet',
      title: 'Term Sheet - $50M Facility',
      status: 'pending_signature',
      uploadedDate: new Date('2024-04-18'),
      fileUrl: '/documents/techcorp-termsheet.pdf'
    },
    {
      id: 'doc3',
      dealId: '3',
      companyName: 'Green Energy Inc',
      type: 'facility_agreement',
      title: 'Draft Purchase Agreement',
      status: 'pending_signature',
      uploadedDate: new Date('2024-04-17'),
      fileUrl: '/documents/greenenergy-draft.pdf'
    },
    {
      id: 'doc4',
      dealId: '4',
      companyName: 'HealthTech Pro',
      type: 'term_sheet',
      title: 'Initial Term Sheet',
      status: 'inactive',
      uploadedDate: new Date('2024-04-10'),
      fileUrl: '/documents/healthtech-termsheet.pdf'
    }
  ];

  const getMockActivities = (): Activity[] => [
    {
      id: '1',
      type: 'exercise_notice',
      dealId: '1',
      companyName: 'BioSig Technologies',
      description: 'VWAP exercise notice received for $5M',
      timestamp: new Date('2024-04-20T10:30:00'),
      priority: 'high'
    },
    {
      id: '2',
      type: 'registration_filed',
      dealId: '2',
      companyName: 'TechCorp Solutions',
      description: 'S-1 registration statement filed with SEC',
      timestamp: new Date('2024-04-19T14:20:00'),
      priority: 'medium'
    },
    {
      id: '3',
      type: 'document_signed',
      dealId: '3',
      companyName: 'Green Energy Inc',
      description: 'Facility agreement executed',
      timestamp: new Date('2024-04-18T09:15:00'),
      priority: 'medium'
    }
  ];

  const getMockTasks = (): Task[] => [
    {
      id: '1',
      dealId: '1',
      companyName: 'BioSig Technologies',
      title: 'Facilitate Stock Transfer',
      description: 'Process VWAP exercise - 500,000 shares',
      assignee: 'Operations Team',
      dueDate: new Date('2024-04-22'),
      status: 'in_progress',
      progress: 45,
      type: 'stock_transfer'
    },
    {
      id: '2',
      dealId: '2',
      companyName: 'TechCorp Solutions',
      title: 'Review Registration Statement',
      description: 'Legal review of S-1 filing',
      assignee: 'Legal Team',
      dueDate: new Date('2024-04-25'),
      status: 'pending',
      progress: 0,
      type: 'document_review'
    },
    {
      id: '3',
      dealId: '1',
      companyName: 'BioSig Technologies',
      title: 'Settlement Processing',
      description: 'Complete wire transfer for exercise',
      assignee: 'Finance Team',
      dueDate: new Date('2024-04-21'),
      status: 'in_progress',
      progress: 70,
      type: 'settlement'
    }
  ];

  const handleDragStart = (e: React.DragEvent, deal: Deal) => {
    setDraggedDeal(deal);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, columnId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverColumn(columnId);
  };

  const handleDragLeave = () => {
    setDragOverColumn(null);
  };

  const handleDrop = async (e: React.DragEvent, newStatus: string) => {
    e.preventDefault();
    setDragOverColumn(null);
    
    if (!draggedDeal || draggedDeal.status === newStatus) return;

    // Update deal status
    const updatedDeals = deals.map(deal => 
      deal.id === draggedDeal.id 
        ? { ...deal, status: newStatus as Deal['status'], daysInStage: 0 }
        : deal
    );
    setDeals(updatedDeals);

    // Log activity
    const newActivity: Activity = {
      id: Date.now().toString(),
      type: 'task_complete',
      dealId: draggedDeal.id,
      companyName: draggedDeal.companyName,
      description: `Deal moved to ${newStatus.replace('_', ' ')}`,
      timestamp: new Date(),
      priority: 'medium',
      user: 'Admin User'
    };
    setActivities(prev => [newActivity, ...prev]);

    // Make API call to update status
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/admin/facilities/${draggedDeal.id}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Error updating deal status:', error);
    }

    setDraggedDeal(null);
  };

  // Temporarily commented - used in exercise tab
  // const handleFileUpload = async (exerciseId: string, fileType: 'wireConfirmation' | 'tradingData', file: File) => {
  //   // Handle file upload logic
  //   console.log(`Uploading ${fileType} for exercise ${exerciseId}`, file);
  //   // Update exercise with file reference
  // };


  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };
  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(num);
  };
  const formatCompactCurrency = (amount: number) => {
    if (amount >= 1000000) {
      const millions = amount / 1000000;
      return `${formatNumber(Number(millions.toFixed(1)))}M`;
    }
    return formatNumber(amount);
  };



  const getActivityIcon = (type: Activity['type']) => {
    switch (type) {
      case 'exercise_notice':
        return <BanknotesIcon className="h-5 w-5 text-green-600" />;
      case 'document_signed':
        return <DocumentTextIcon className="h-5 w-5 text-blue-600" />;
      case 'registration_filed':
        return <DocumentDuplicateIcon className="h-5 w-5 text-purple-600" />;
      case 'settlement_complete':
        return <CheckCircleIcon className="h-5 w-5 text-green-600" />;
      default:
        return <BellIcon className="h-5 w-5 text-gray-600" />;
    }
  };

  const getTaskPriorityColor = (task: Task) => {
    const daysUntilDue = Math.ceil((task.dueDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
    if (daysUntilDue < 0) return 'text-red-600 bg-red-50';
    if (daysUntilDue <= 1) return 'text-orange-600 bg-orange-50';
    if (daysUntilDue <= 3) return 'text-yellow-600 bg-yellow-50';
    return 'text-green-600 bg-green-50';
  };

  const filteredDeals = deals.filter(deal =>
    deal.companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    deal.ticker.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredFacilities = facilities.filter(facility => {
    const matchesSearch = facility.companyName.toLowerCase().includes(facilitySearchTerm.toLowerCase()) ||
      facility.ticker.toLowerCase().includes(facilitySearchTerm.toLowerCase()) ||
      facility.status.toLowerCase().includes(facilitySearchTerm.toLowerCase()) ||
      facility.stage.toLowerCase().includes(facilitySearchTerm.toLowerCase());
    
    const matchesStatus = facilityStatusFilter === 'all' || facility.stage === facilityStatusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const filteredTransactions = transactions.filter(transaction => {
    // Search filter
    const matchesSearch = transaction.companyName.toLowerCase().includes(transactionSearchTerm.toLowerCase()) ||
      transaction.ticker.toLowerCase().includes(transactionSearchTerm.toLowerCase()) ||
      transaction.transactionType.toLowerCase().includes(transactionSearchTerm.toLowerCase());
    
    // Status filter
    const matchesStatus = transactionStatusFilter === 'all' || transaction.stage === transactionStatusFilter;
    
    // Shares filter
    let matchesShares = true;
    if (transactionSharesFilter !== 'all') {
      const shares = transaction.sharesRequested;
      switch (transactionSharesFilter) {
        case 'small':
          matchesShares = shares < 100000;
          break;
        case 'medium':
          matchesShares = shares >= 100000 && shares < 500000;
          break;
        case 'large':
          matchesShares = shares >= 500000;
          break;
      }
    }
    
    // Volume progress filter
    let matchesVolume = true;
    if (transactionVolumeFilter !== 'all') {
      const progress = transaction.volumeProgress;
      switch (transactionVolumeFilter) {
        case 'low':
          matchesVolume = progress < 25;
          break;
        case 'medium':
          matchesVolume = progress >= 25 && progress < 75;
          break;
        case 'high':
          matchesVolume = progress >= 75 && progress < 100;
          break;
        case 'complete':
          matchesVolume = progress >= 100;
          break;
      }
    }
    
    // Exercise date filter
    let matchesDate = true;
    if (transactionDateFilter !== 'all') {
      const exerciseDate = new Date(transaction.exerciseDate);
      const now = new Date();
      const daysDiff = Math.floor((now.getTime() - exerciseDate.getTime()) / (1000 * 60 * 60 * 24));
      
      switch (transactionDateFilter) {
        case 'today':
          matchesDate = daysDiff === 0;
          break;
        case 'week':
          matchesDate = daysDiff <= 7;
          break;
        case 'month':
          matchesDate = daysDiff <= 30;
          break;
        case 'quarter':
          matchesDate = daysDiff <= 90;
          break;
      }
    }
    
    return matchesSearch && matchesStatus && matchesShares && matchesVolume && matchesDate;
  });

  // Calculate pipeline values for facilities view
  const pipelineDataForFacilities = pipelineStages.map(stage => {
    const stageFacilities = filteredFacilities.filter(facility => facility.status === stage.id);
    const stageDeals = stageFacilities.map(facility => ({
      id: facility.id,
      companyName: facility.companyName,
      ticker: facility.ticker,
      facilitySize: facility.facilitySize,
      status: facility.status as Deal['status'],
      stage: facility.stage,
      daysInStage: facility.daysInStage,
      contact: {
        name: facility.contact.name,
        phone: facility.contact.phone
      },
      lastActivity: new Date(facility.lastActivity),
      nextAction: facility.nextAction,
      progress: facility.progress
    }));
    const totalValue = stageDeals.reduce((sum, deal) => sum + deal.facilitySize, 0);
    const weightedValue = totalValue * (stage.probability / 100);
    return {
      ...stage,
      deals: stageDeals,
      value: totalValue,
      weightedValue
    };
  });

  // Calculate pipeline values for main pipeline tab
  const pipelineData = pipelineStages.map(stage => {
    const stageDeals = filteredDeals.filter(deal => deal.status === stage.id);
    const totalValue = stageDeals.reduce((sum, deal) => sum + deal.facilitySize, 0);
    const weightedValue = totalValue * (stage.probability / 100);
    return {
      ...stage,
      deals: stageDeals,
      value: totalValue,
      weightedValue
    };
  });

  const totalPipelineValue = pipelineData.reduce((sum, stage) => sum + stage.value, 0);
  const totalWeightedValue = pipelineData.reduce((sum, stage) => sum + stage.weightedValue, 0);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center gap-4">
              <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span className="font-medium">{formatCompactCurrency(totalPipelineValue)}</span>
                <span className="text-gray-400">•</span>
                <span>{formatNumber(filteredDeals.length)} facilities</span>
                <span className="text-gray-400">•</span>
                <span className="text-green-600 font-medium">
                  {formatCompactCurrency(totalWeightedValue)} weighted
                </span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-64"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8 overflow-x-auto">
            {[
              { id: 'facilities', label: 'Facilities', icon: UserGroupIcon },
              { id: 'transactions', label: 'Transactions', icon: BanknotesIcon },
              { id: 'activities', label: 'Activities', icon: BellIcon },
              { id: 'users', label: 'Users', icon: UserGroupIcon },
              // { id: 'exercises', label: 'Exercise Tracker', icon: ChartBarIcon },
              { id: 'documents', label: 'Documents', icon: DocumentTextIcon },
   
              { id: 'tasks', label: 'Tasks', icon: CheckCircleIcon },
              { id: 'analytics', label: 'Analytics', icon: ArrowTrendingUpIcon },
              { id: 'web-analytics', label: 'Web Analytics', icon: GlobeAltIcon },
              { id: 'crm', label: 'CRM', icon: UserGroupIcon },
              { id: 'support', label: 'Support', icon: TicketIcon }
             
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setSelectedTab(tab.id as any)}
                className={`py-3 px-1 border-b-2 font-medium text-sm flex items-center gap-2 whitespace-nowrap ${
                  selectedTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="h-5 w-5" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">

        {/* Exercise Tracker - TEMPORARILY HIDDEN 
        {selectedTab === 'exercises' && (
          <div className="p-6">
            <div className="mb-6 flex justify-between items-center">
              <div className="flex items-center gap-4">
                <h2 className="text-xl font-semibold text-gray-900">Exercise Notices</h2>
                <div className="flex bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => setExerciseViewMode('active')}
                    className={`px-4 py-2 rounded ${exerciseViewMode === 'active' ? 'bg-white shadow-sm font-medium' : ''}`}
                  >
                    Active ({formatNumber(exercises.filter(e => e.status === 'active').length)})
                  </button>
                  <button
                    onClick={() => setExerciseViewMode('completed')}
                    className={`px-4 py-2 rounded ${exerciseViewMode === 'completed' ? 'bg-white shadow-sm font-medium' : ''}`}
                  >
                    Completed ({formatNumber(exercises.filter(e => e.status === 'completed').length)})
                  </button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {exercises
                .filter(exercise => exercise.status === exerciseViewMode)
                .map((exercise) => (
                  <div key={exercise.id} className="bg-white rounded-lg shadow-lg p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="font-semibold text-lg text-gray-900">
                          {exercise.companyName} - {exercise.type} Exercise
                        </h3>
                        <p className="text-sm text-gray-600">{exercise.ticker}</p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        exercise.status === 'active' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {exercise.status === 'active' ? 'Active' : 'Completed'}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-xs text-gray-500">Shares</p>
                        <p className="font-medium">{exercise.shares.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Estimated Amount</p>
                        <p className="font-medium">{formatCurrency(exercise.estimatedAmount)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Valuation Period</p>
                        <p className="font-medium text-sm">
                          {new Date(exercise.valuationStartDate).toLocaleDateString()} - {new Date(exercise.valuationEndDate).toLocaleDateString()}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Settlement Date</p>
                        <p className="font-medium">{new Date(exercise.settlementDate).toLocaleDateString()}</p>
                      </div>
                    </div>

                    {exercise.type !== 'Advance' && (
                      <div className="mb-4">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm text-gray-600">Volume Progress</span>
                          <span className="text-sm font-medium">
                            {exercise.currentVolume.toLocaleString()} / {exercise.volumeThreshold.toLocaleString()}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div
                            className="bg-blue-600 h-3 rounded-full transition-all flex items-center justify-center"
                            style={{ width: `${exercise.volumeProgress}%` }}
                          >
                            <span className="text-xs text-white font-medium">
                              {formatNumber(exercise.volumeProgress)}%
                            </span>
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Updates every 15 minutes</p>
                      </div>
                    )}

                    {exercise.status === 'completed' && (
                      <div className="border-t pt-4">
                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div>
                            <p className="text-xs text-gray-500">Final Price</p>
                            <p className="font-medium">${exercise.finalPrice?.toFixed(2)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Final Proceeds</p>
                            <p className="font-medium">{formatCurrency(exercise.finalProceeds || 0)}</p>
                          </div>
                        </div>

                        <div className="space-y-3">
                          <div>
                            <label className="text-sm font-medium text-gray-700">Wire Confirmation</label>
                            {exercise.wireConfirmation ? (
                              <p className="text-sm text-green-600 flex items-center gap-1 mt-1">
                                <CheckCircleIcon className="h-4 w-4" />
                                {exercise.wireConfirmation}
                              </p>
                            ) : (
                              <input
                                type="file"
                                onChange={(e) => e.target.files && handleFileUpload(exercise.id, 'wireConfirmation', e.target.files[0])}
                                className="mt-1 block w-full text-sm"
                              />
                            )}
                          </div>

                          <div>
                            <label className="text-sm font-medium text-gray-700">Trading Data</label>
                            {exercise.tradingData ? (
                              <div className="mt-1">
                                <p className="text-sm text-gray-600">
                                  Avg Sale Price: ${exercise.tradingData.avgSalePrice?.toFixed(2)}
                                </p>
                                <p className={`text-sm font-medium ${exercise.pnl && exercise.pnl > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  P&L: {formatCurrency(exercise.pnl || 0)}
                                </p>
                              </div>
                            ) : (
                              <input
                                type="file"
                                onChange={(e) => e.target.files && handleFileUpload(exercise.id, 'tradingData', e.target.files[0])}
                                className="mt-1 block w-full text-sm"
                              />
                            )}
                          </div>
                        </div>

                        <button className="mt-4 w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2">
                          <DocumentTextIcon className="h-5 w-5" />
                          Generate Settlement Statement
                        </button>
                      </div>
                    )}

                    {exercise.status === 'active' && exercise.volumeProgress === 100 && (
                      <button 
                        onClick={() => console.log('Complete valuation for:', exercise.id)}
                        className="mt-4 w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                      >
                        Complete Valuation
                      </button>
                    )}
                  </div>
                ))}
            </div>
          </div>
        )}
        */}

        {/* Documents Section */}
        {selectedTab === 'documents' && (
          <div className="p-6">
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Document Management</h2>
              
              {/* Pending Signatures */}
              <div className="mb-8">
                <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center gap-2">
                  <ExclamationCircleIcon className="h-5 w-5 text-orange-500" />
                  Pending Signatures ({formatNumber(documents.filter(d => d.status === 'pending_signature').length)})
                </h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {documents
                    .filter(doc => doc.status === 'pending_signature')
                    .map((doc) => (
                      <div key={doc.id} className="bg-white rounded-lg shadow p-4 border-l-4 border-orange-500">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900">{doc.title}</h4>
                            <p className="text-sm text-gray-600">{doc.companyName}</p>
                            <p className="text-xs text-gray-500 mt-1">
                              Uploaded {new Date(doc.uploadedDate).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <button className="p-2 text-blue-600 hover:bg-blue-50 rounded">
                              <ArrowDownTrayIcon className="h-5 w-5" />
                            </button>
                            <button className="px-3 py-1 bg-orange-100 text-orange-700 rounded text-sm font-medium hover:bg-orange-200">
                              Send Reminder
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>

              {/* Executed Documents */}
              <div className="mb-8">
                <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center gap-2">
                  <DocumentCheckIcon className="h-5 w-5 text-green-600" />
                  Executed Documents
                </h3>
                <div className="bg-white rounded-lg shadow overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Document
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Company
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Type
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Executed Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {documents
                        .filter(doc => doc.status === 'executed')
                        .map((doc) => (
                          <tr key={doc.id}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {doc.title}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {doc.companyName}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                                {doc.type.replace('_', ' ')}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {doc.executedDate && new Date(doc.executedDate).toLocaleDateString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <button className="text-blue-600 hover:text-blue-900">
                                <ArrowDownTrayIcon className="h-5 w-5" />
                              </button>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Inactive Documents */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">Inactive Documents</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  {documents
                    .filter(doc => doc.status === 'inactive')
                    .map((doc) => (
                      <div key={doc.id} className="flex justify-between items-center py-2">
                        <div>
                          <p className="text-sm text-gray-600">{doc.title}</p>
                          <p className="text-xs text-gray-500">{doc.companyName}</p>
                        </div>
                        <button className="text-sm text-blue-600 hover:text-blue-700">
                          Reactivate
                        </button>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Activities View */}
        {selectedTab === 'activities' && (
          <div className="p-6">
            <div className="bg-white rounded-lg shadow">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activities</h3>
                <div className="space-y-4">
                  {activities.map((activity) => (
                    <div key={activity.id} className="flex items-start gap-4 p-4 hover:bg-gray-50 rounded-lg">
                      <div className="flex-shrink-0">
                        {getActivityIcon(activity.type)}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium text-gray-900">{activity.companyName}</p>
                            <p className="text-sm text-gray-600">{activity.description}</p>
                            {activity.user && (
                              <p className="text-xs text-gray-500 mt-1">by {activity.user}</p>
                            )}
                          </div>
                          <span className="text-xs text-gray-500">
                            {new Date(activity.timestamp).toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tasks View */}
        {selectedTab === 'tasks' && (
          <div className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {tasks.map((task) => (
                <div key={task.id} className="bg-white rounded-lg shadow p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h4 className="font-semibold text-gray-900">{task.title}</h4>
                      <p className="text-sm text-gray-600">{task.companyName}</p>
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTaskPriorityColor(task)}`}>
                      Due: {new Date(task.dueDate).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 mb-4">{task.description}</p>
                  <div className="mb-4">
                    <div className="flex justify-between text-sm text-gray-600 mb-1">
                      <span>Progress</span>
                      <span>{formatNumber(task.progress)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-green-600 h-2 rounded-full transition-all"
                        style={{ width: `${task.progress}%` }}
                      />
                    </div>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-600">Assigned to: {task.assignee}</span>
                    <button className="text-blue-600 hover:text-blue-700 font-medium">
                      Update
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Facilities View */}
        {selectedTab === 'facilities' && (
          <div className="p-6">
            <div className="mb-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Facilities</h2>
                <div className="flex items-center gap-4">
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">{formatNumber(filteredFacilities.length)}</span> of {formatNumber(facilities.length)} facilities
                  </div>
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">
                      {formatCompactCurrency(filteredFacilities.reduce((sum, f) => sum + f.facilitySize, 0))}
                    </span> total value
                  </div>
                </div>
              </div>
              
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-white rounded-lg shadow p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Total Facilities</p>
                      <p className="text-2xl font-bold text-gray-900">{formatNumber(facilities.length)}</p>
                    </div>
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <DocumentTextIcon className="h-6 w-6 text-blue-600" />
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-lg shadow p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Total Value</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {formatCompactCurrency(facilities.reduce((sum, f) => sum + f.facilitySize, 0))}
                      </p>
                    </div>
                    <div className="p-2 bg-green-100 rounded-lg">
                      <CurrencyDollarIcon className="h-6 w-6 text-green-600" />
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-lg shadow p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Active Facilities</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {formatNumber(facilities.filter(f => f.stage === 'active').length)}
                      </p>
                    </div>
                    <div className="p-2 bg-green-100 rounded-lg">
                      <CheckCircleIcon className="h-6 w-6 text-green-600" />
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-lg shadow p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Pending Review</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {formatNumber(facilities.filter(f => f.stage === 'lead_in').length)}
                      </p>
                    </div>
                    <div className="p-2 bg-orange-100 rounded-lg">
                      <ClockIcon className="h-6 w-6 text-orange-600" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Search, Filter and View Toggle Bar */}
              <div className="mb-4 flex gap-4 items-center justify-between">
                <div className="flex gap-4 items-center">
                  <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search facilities by company, ticker, status, or stage..."
                      value={facilitySearchTerm}
                      onChange={(e) => setFacilitySearchTerm(e.target.value)}
                      className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full max-w-md"
                    />
                  </div>
                  
                  <select
                    value={facilityStatusFilter}
                    onChange={(e) => setFacilityStatusFilter(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">All Stages</option>
                    <option value="lead_in">Lead In</option>
                    <option value="docs_out">Docs Out</option>
                    <option value="awaiting_registration">Awaiting Registration</option>
                    <option value="active">Active</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>

                {/* View Toggle */}
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-gray-700">Show Pipeline View</span>
                  <button
                    onClick={() => setFacilityViewMode(facilityViewMode === 'table' ? 'pipeline' : 'table')}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      facilityViewMode === 'pipeline' 
                        ? 'bg-blue-600' 
                        : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        facilityViewMode === 'pipeline' 
                          ? 'translate-x-6' 
                          : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>
              
              {facilities.length === 0 ? (
                <div className="bg-white rounded-lg shadow p-8 text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading facilities...</p>
                </div>
              ) : facilityViewMode === 'table' ? (
                /* Table View */
                <div className="bg-white rounded-lg shadow overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Company Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Ticker
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Facility Size
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Stage
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Days in Stage
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Last Activity
                        </th>
                        {/* Progress column removed */}
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredFacilities.map((facility) => (
                        <tr 
                          key={facility.id} 
                          className="hover:bg-gray-50 cursor-pointer transition-colors"
                          onClick={() => {
                            setSelectedFacility(facility);
                            setShowFacilityModal(true);
                          }}
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">{facility.companyName}</div>
                              <div className="text-xs text-gray-500">{facility.nextAction}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {facility.ticker}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {formatCompactCurrency(facility.facilitySize)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {facility.stage}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              facility.daysInStage > 10 ? 'bg-red-100 text-red-800' :
                              facility.daysInStage > 5 ? 'bg-orange-100 text-orange-800' :
                              'bg-green-100 text-green-800'
                            }`}>
                              {formatNumber(facility.daysInStage)}d
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(facility.lastActivity).toLocaleDateString()}
                          </td>
                          {/* Progress cell removed */}
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex gap-2">
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // Handle document action
                                }}
                                className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded"
                              >
                                <DocumentTextIcon className="h-4 w-4" />
                              </button>
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // Handle complete action
                                }}
                                className="text-green-600 hover:text-green-900 p-1 hover:bg-green-50 rounded"
                              >
                                <CheckCircleIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                /* Pipeline View */
                <div className="h-full overflow-x-auto overflow-y-hidden">
                  <div className="inline-flex h-full min-w-full px-4 py-4 gap-2">
                    {pipelineDataForFacilities.map((stage) => (
                      <div
                        key={stage.id}
                        className={`w-80 flex-shrink-0 bg-gray-50 rounded-lg flex flex-col ${
                          dragOverColumn === stage.id ? 'ring-2 ring-blue-500' : ''
                        }`}
                        onDragOver={(e) => handleDragOver(e, stage.id)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, stage.id)}
                      >
                        {/* Column Header */}
                        <div className="p-4 border-b border-gray-200">
                          <div className="flex items-center justify-between mb-1">
                            <h3 className={`font-semibold ${stage.color}`}>{stage.title}</h3>
                            <span className={`text-sm font-medium ${stage.color}`}>
                              {formatNumber(stage.deals.length)}
                            </span>
                          </div>
                          <div className="text-sm text-gray-600">
                            {formatCompactCurrency(stage.value)}
                            {stage.probability < 100 && (
                              <span className="text-gray-400 ml-1">• {stage.probability}%</span>
                            )}
                          </div>
                        </div>

                        {/* Deals */}
                        <div className="flex-1 overflow-y-auto p-2 space-y-2">
                          {stage.deals.map((deal) => (
                            <div
                              key={deal.id}
                              draggable
                              onDragStart={(e) => handleDragStart(e as React.DragEvent, deal)}
                              className={`${stage.bgColor} rounded-lg p-4 cursor-move shadow-sm hover:shadow-md transition-all transform hover:scale-[1.02] active:scale-[1.05] active:opacity-80`}
                            >
                              <div className="flex justify-between items-start mb-2">
                                <div className="flex-1">
                                  <h4 className="font-medium text-gray-900 text-sm">{deal.companyName}</h4>
                                  <p className="text-xs text-gray-500">{deal.ticker}</p>
                                </div>
                                <span className="text-sm font-semibold text-gray-900">
                                  {formatCompactCurrency(deal.facilitySize)}
                                </span>
                              </div>
                              
                              <div className="flex items-center gap-2 text-xs text-gray-600 mb-2">
                                <UserIcon className="h-3 w-3" />
                                <span>{deal.contact.name}</span>
                              </div>

                              <div className="flex items-center justify-between text-xs">
                                <span className="text-gray-500">
                                  {formatNumber(deal.daysInStage)}d in stage
                                </span>
                              </div>

                              <div className="mt-2 flex gap-2">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    // Email functionality removed since email not available in facility data
                                  }}
                                  className="p-1 hover:bg-white/50 rounded opacity-50 cursor-not-allowed"
                                  disabled
                                >
                                  <EnvelopeIcon className="h-4 w-4 text-gray-600" />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    window.location.href = `tel:${deal.contact.phone}`;
                                  }}
                                  className="p-1 hover:bg-white/50 rounded"
                                >
                                  <PhoneIcon className="h-4 w-4 text-gray-600" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Transactions View */}
        {selectedTab === 'transactions' && (
          <div className="p-6">
            <div className="mb-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Transactions</h2>
                <div className="flex items-center gap-4">
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">{formatNumber(filteredTransactions.length)}</span> of {formatNumber(transactions.length)} transactions
                  </div>
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">
                      {formatCompactCurrency(filteredTransactions.reduce((sum, t) => sum + t.potentialProceeds, 0))}
                    </span> potential proceeds
                  </div>
                </div>
              </div>
              
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-white rounded-lg shadow p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Filtered Transactions</p>
                      <p className="text-2xl font-bold text-gray-900">{formatNumber(filteredTransactions.length)}</p>
                    </div>
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <BanknotesIcon className="h-6 w-6 text-blue-600" />
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-lg shadow p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Active (Filtered)</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {formatNumber(filteredTransactions.filter(t => t.stage === 'Valuation_Period' || t.stage === 'Settlement_Period').length)}
                      </p>
                    </div>
                    <div className="p-2 bg-green-100 rounded-lg">
                      <CheckCircleIcon className="h-6 w-6 text-green-600" />
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-lg shadow p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Shares (Filtered)</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {filteredTransactions.reduce((sum, t) => sum + t.sharesRequested, 0).toLocaleString()}
                      </p>
                    </div>
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <ChartBarIcon className="h-6 w-6 text-purple-600" />
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-lg shadow p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Avg Volume (Filtered)</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {filteredTransactions.length > 0 
                          ? Math.round(filteredTransactions.reduce((sum, t) => sum + t.volumeProgress, 0) / filteredTransactions.length)
                          : 0}%
                      </p>
                    </div>
                    <div className="p-2 bg-orange-100 rounded-lg">
                      <ArrowTrendingUpIcon className="h-6 w-6 text-orange-600" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Search and Filter Bar */}
              <div className="mb-6 bg-white rounded-lg shadow p-4">
                <div className="grid grid-cols-1 lg:grid-cols-6 gap-4">
                  {/* Search */}
                  <div className="lg:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                    <div className="relative">
                      <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Company name, ticker, or type..."
                        value={transactionSearchTerm}
                        onChange={(e) => setTransactionSearchTerm(e.target.value)}
                        className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full text-sm"
                      />
                    </div>
                  </div>

                  {/* Status Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                      value={transactionStatusFilter}
                      onChange={(e) => setTransactionStatusFilter(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    >
                      <option value="all">All Statuses</option>
                      <option value="Pending">Pending</option>
                      <option value="Valuation_Period">In Valuation</option>
                      <option value="Settlement_Period">In Settlement</option>
                      <option value="Completed">Completed</option>
                      <option value="Failed">Failed</option>
                      <option value="Cancelled">Cancelled</option>
                    </select>
                  </div>

                  {/* Shares Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Shares</label>
                    <select
                      value={transactionSharesFilter}
                      onChange={(e) => setTransactionSharesFilter(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    >
                      <option value="all">All Sizes</option>
                      <option value="small">&lt; 100K shares</option>
                      <option value="medium">100K - 500K shares</option>
                      <option value="large">&gt; 500K shares</option>
                    </select>
                  </div>

                  {/* Volume Progress Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Volume Progress</label>
                    <select
                      value={transactionVolumeFilter}
                      onChange={(e) => setTransactionVolumeFilter(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    >
                      <option value="all">All Progress</option>
                      <option value="low">0-25%</option>
                      <option value="medium">25-75%</option>
                      <option value="high">75-99%</option>
                      <option value="complete">100%</option>
                    </select>
                  </div>

                  {/* Exercise Date Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Exercise Date</label>
                    <select
                      value={transactionDateFilter}
                      onChange={(e) => setTransactionDateFilter(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    >
                      <option value="all">All Dates</option>
                      <option value="today">Today</option>
                      <option value="week">Last 7 days</option>
                      <option value="month">Last 30 days</option>
                      <option value="quarter">Last 90 days</option>
                    </select>
                  </div>
                </div>
                
                {/* Clear Filters Button */}
                {(transactionSearchTerm || transactionStatusFilter !== 'all' || transactionSharesFilter !== 'all' || 
                  transactionVolumeFilter !== 'all' || transactionDateFilter !== 'all') && (
                  <div className="mt-3 flex justify-end">
                    <button
                      onClick={() => {
                        setTransactionSearchTerm('');
                        setTransactionStatusFilter('all');
                        setTransactionSharesFilter('all');
                        setTransactionVolumeFilter('all');
                        setTransactionDateFilter('all');
                      }}
                      className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors"
                    >
                      Clear all filters
                    </button>
                  </div>
                )}
              </div>

              {/* Transactions Table */}
              {transactions.length === 0 ? (
                <div className="bg-white rounded-lg shadow p-8 text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading transactions...</p>
                </div>
              ) : (
                <div className="bg-white rounded-lg shadow overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Company
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Type
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Shares
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Potential Proceeds
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Volume Progress
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Exercise Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Next Action
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredTransactions.map((transaction) => (
                        <tr key={transaction.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">{transaction.companyName}</div>
                              <div className="text-xs text-gray-500">{transaction.ticker}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                              {transaction.transactionType}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              transaction.stage === 'Completed'
                                ? 'bg-green-100 text-green-800'
                                : transaction.stage === 'Valuation_Period'
                                  ? 'bg-blue-100 text-blue-800'
                                  : transaction.stage === 'Settlement_Period'
                                    ? 'bg-orange-100 text-orange-800'
                                  : transaction.stage === 'Failed'
                                    ? 'bg-red-100 text-red-800'
                                  : 'bg-gray-100 text-gray-800'
                            }`}>
                              {transaction.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {transaction.sharesRequested.toLocaleString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {formatCurrency(transaction.potentialProceeds)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                                <div
                                  className={`h-2 rounded-full transition-all ${
                                    transaction.volumeThresholdMet ? 'bg-green-600' : 'bg-blue-600'
                                  }`}
                                  style={{ width: `${transaction.volumeProgress}%` }}
                                />
                              </div>
                              <span className="text-xs text-gray-500">{formatNumber(parseFloat(transaction.volumeProgress.toFixed(1)))}%</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <div>
                              <div>{new Date(transaction.exerciseDate).toLocaleDateString()}</div>
                              <div className="text-xs text-gray-400">{formatNumber(transaction.daysSinceExercise)}d ago</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <div className="max-w-xs truncate" title={transaction.nextAction}>
                              {transaction.nextAction}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex gap-2">
                              <button className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded">
                                <DocumentTextIcon className="h-4 w-4" />
                              </button>
                              {transaction.stage === 'Valuation_Period' && transaction.volumeThresholdMet && (
                                <button className="text-green-600 hover:text-green-900 p-1 hover:bg-green-50 rounded">
                                  <CheckCircleIcon className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Analytics View */}
        {selectedTab === 'analytics' && (
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-medium text-gray-600">Total Pipeline Value</h3>
                  <CurrencyDollarIcon className="h-8 w-8 text-green-600" />
                </div>
                <p className="text-2xl font-bold text-gray-900">
                  {formatCurrency(totalPipelineValue)}
                </p>
                <p className="text-sm text-gray-600 mt-2">Across {formatNumber(deals.length)} deals</p>
              </div>
              
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-medium text-gray-600">Active Exercises</h3>
                  <ChartBarIcon className="h-8 w-8 text-blue-600" />
                </div>
                <p className="text-2xl font-bold text-gray-900">
                  {formatNumber(exercises.filter(e => e.status === 'active').length)}
                </p>
                <p className="text-sm text-gray-600 mt-2">In progress</p>
              </div>
              
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-medium text-gray-600">Pending Documents</h3>
                  <DocumentTextIcon className="h-8 w-8 text-orange-600" />
                </div>
                <p className="text-2xl font-bold text-gray-900">
                  {formatNumber(documents.filter(d => d.status === 'pending_signature').length)}
                </p>
                <p className="text-sm text-gray-600 mt-2">Awaiting signature</p>
              </div>
              
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-medium text-gray-600">Total P&L</h3>
                  <CalculatorIcon className="h-8 w-8 text-purple-600" />
                </div>
                <p className={`text-2xl font-bold ${
                  exercises.reduce((sum, e) => sum + (e.pnl || 0), 0) >= 0 
                    ? 'text-green-600' 
                    : 'text-red-600'
                }`}>
                  {formatCurrency(exercises.reduce((sum, e) => sum + (e.pnl || 0), 0))}
                </p>
                <p className="text-sm text-gray-600 mt-2">From completed exercises</p>
              </div>
            </div>
          </div>
        )}

        {/* Web Analytics Tab */}
        {selectedTab === 'web-analytics' && (
          <WebAnalytics />
        )}

        {/* CRM Tab */}
        {selectedTab === 'crm' && (
          <CRM />
        )}

        {/* Support Tab */}
        {selectedTab === 'support' && (
          <SupportTickets />
        )}

        {/* User Management Tab */}
        {selectedTab === 'users' && (
          <UserManagement />
        )}
      </div>

      {/* Facility Details Modal */}
      {showFacilityModal && selectedFacility && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{selectedFacility.companyName}</h2>
                <div className="flex items-center gap-4 mt-2">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {selectedFacility.ticker}
                  </span>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    selectedFacility.status === 'active'
                      ? 'bg-green-100 text-green-800'
                      : selectedFacility.status === 'awaiting_registration'
                        ? 'bg-orange-100 text-orange-800'
                        : selectedFacility.status === 'docs_out'
                          ? 'bg-blue-100 text-blue-800'
                        : 'bg-gray-100 text-gray-800'
                  }`}>
                    {selectedFacility.status.replace('_', ' ')}
                  </span>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowFacilityModal(false);
                  setSelectedFacility(null);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-blue-600">Facility Size</p>
                      <p className="text-2xl font-bold text-blue-900">
                        {formatCompactCurrency(selectedFacility.facilitySize)}
                      </p>
                    </div>
                    <CurrencyDollarIcon className="h-8 w-8 text-blue-600" />
                  </div>
                </div>

                <div className="bg-green-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-green-600">Progress</p>
                      <p className="text-2xl font-bold text-green-900">{formatNumber(selectedFacility.progress)}%</p>
                    </div>
                    <ChartBarIcon className="h-8 w-8 text-green-600" />
                  </div>
                </div>

                <div className="bg-orange-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-orange-600">Days in Stage</p>
                      <p className="text-2xl font-bold text-orange-900">{formatNumber(selectedFacility.daysInStage)}</p>
                    </div>
                    <ClockIcon className="h-8 w-8 text-orange-600" />
                  </div>
                </div>
              </div>

              {/* Detailed Information */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Basic Information */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <DocumentTextIcon className="h-5 w-5 text-gray-600" />
                    Basic Information
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Company Name:</span>
                      <span className="text-sm font-medium text-gray-900">{selectedFacility.companyName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Ticker:</span>
                      <span className="text-sm font-medium text-gray-900">{selectedFacility.ticker}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Facility ID:</span>
                      <span className="text-sm font-medium text-gray-900 font-mono">{selectedFacility.id}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Current Stage:</span>
                      <span className="text-sm font-medium text-gray-900">{selectedFacility.stage}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Status:</span>
                      <span className={`text-sm font-medium ${
                        selectedFacility.status === 'active' ? 'text-green-600' :
                        selectedFacility.status === 'awaiting_registration' ? 'text-orange-600' :
                        selectedFacility.status === 'docs_out' ? 'text-blue-600' : 'text-gray-600'
                      }`}>
                        {selectedFacility.status.replace('_', ' ')}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <UserIcon className="h-5 w-5 text-gray-600" />
                    Contact Information
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Contact Name:</span>
                      <span className="text-sm font-medium text-gray-900">{selectedFacility.contact.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Phone:</span>
                      <a 
                        href={`tel:${selectedFacility.contact.phone}`}
                        className="text-sm font-medium text-blue-600 hover:text-blue-800"
                      >
                        {selectedFacility.contact.phone}
                      </a>
                    </div>
                  </div>
                  
                  {/* Contact Actions */}
                  <div className="mt-4 flex gap-2">
                    <button 
                      onClick={() => window.location.href = `tel:${selectedFacility.contact.phone}`}
                      className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      <PhoneIcon className="h-4 w-4" />
                      Call
                    </button>
                  </div>
                </div>

                {/* Activity Information */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <BellIcon className="h-5 w-5 text-gray-600" />
                    Activity Information
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Last Activity:</span>
                      <span className="text-sm font-medium text-gray-900">
                        {new Date(selectedFacility.lastActivity).toLocaleDateString()} at{' '}
                        {new Date(selectedFacility.lastActivity).toLocaleTimeString()}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Days in Current Stage:</span>
                      <span className={`text-sm font-medium ${
                        selectedFacility.daysInStage > 10 ? 'text-red-600' :
                        selectedFacility.daysInStage > 5 ? 'text-orange-600' : 'text-green-600'
                      }`}>
                        {formatNumber(selectedFacility.daysInStage)} days
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Next Action:</span>
                      <span className="text-sm font-medium text-gray-900">{selectedFacility.nextAction}</span>
                    </div>
                  </div>
                </div>

                {/* Progress Information */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <ArrowTrendingUpIcon className="h-5 w-5 text-gray-600" />
                    Progress Tracking
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between text-sm text-gray-600 mb-2">
                        <span>Overall Progress</span>
                        <span>{formatNumber(selectedFacility.progress)}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div
                          className="bg-blue-600 h-3 rounded-full transition-all"
                          style={{ width: `${selectedFacility.progress}%` }}
                        />
                      </div>
                    </div>
                    
                    {/* Exercises Information */}
                    <div>
                      <span className="text-sm text-gray-600">Total Exercises:</span>
                      <span className="text-sm font-medium text-gray-900 ml-2">
                        {formatNumber(selectedFacility.exercises?.length || 0)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="mt-8 flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowFacilityModal(false);
                    setSelectedFacility(null);
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Close
                </button>
                <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2">
                  <DocumentTextIcon className="h-4 w-4" />
                  View Documents
                </button>
                <button className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2">
                  <CheckCircleIcon className="h-4 w-4" />
                  Update Status
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard; 